/**
 * plotAlign — the two widths that make a pillar trend line up with the matrix it sits in.
 *
 * The plot is a row of the same table as the numbers, so a reader compares them by eye and
 * expects January over January. Recharts cannot know where the columns are, so the matrix
 * measures them and the chart is told:
 *
 *   labelWidth  — the Metric column. The y axis is drawn into it, so the plot area starts
 *                 exactly where the first period column starts.
 *   columnWidth — one period column. Half of it pads each end of the x axis, which moves
 *                 every point off the plot edge and onto its own column's centre.
 *
 * Without the padding the first and last points sit hard against the plot edges, half a
 * column adrift from the values they explain — the misalignment is worst exactly where a
 * reader looks first.
 */

import { useLayoutEffect, useState, type RefObject } from 'react'

export interface PlotAlign {
  /** Width of the Metric column in px — the y axis is drawn inside it. */
  labelWidth: number
  /** Width of one period column in px. */
  columnWidth: number
}

/** Used until the table has been measured, and by any caller that has no matrix. */
export const AXIS_FALLBACK = 52

/** Measure the header. The table owns the widths; the chart is only told them. Never sets
 *  state to an equal pair, so an observer cannot loop.
 *
 *  `columns` is a dependency, not decoration: weekly serves more periods than monthly in
 *  the same table box, so the columns narrow while the element the observer watches keeps
 *  its size. Without this the chart would keep the monthly pitch on a weekly matrix. */
export function useColumnWidths(table: RefObject<HTMLTableElement | null>, columns: number): PlotAlign | null {
  const [align, setAlign] = useState<PlotAlign | null>(null)
  useLayoutEffect(() => {
    const node = table.current
    if (!node) return
    const measure = () => {
      const heads = node.querySelectorAll('thead th')
      if (heads.length < 2) return
      const labelWidth = heads[0].getBoundingClientRect().width
      const columnWidth = heads[1].getBoundingClientRect().width
      setAlign((current) =>
        current && current.labelWidth === labelWidth && current.columnWidth === columnWidth
          ? current
          : { labelWidth, columnWidth }
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [table, columns])
  return align
}
